import axios from "axios"
import createAuthRefreshInterceptor from "axios-auth-refresh"

import { config } from "../config"

import { ACCESS_TOKEN, REFRESH_TOKEN } from "./constants"

import { applyMiddleware, createStore } from "redux"
import axiosMiddleware from "redux-axios-middleware"
import concatenateReducers from "redux-concatenate-reducers"
import { composeWithDevTools } from "redux-devtools-extension"

import mainReducer from "./reducers/mainReducer"

const rootReducer = concatenateReducers([mainReducer])
const signature = config.REACT_APP_GOTRUCK_SIGNATURE

export const axios_client = axios.create({
   baseURL: config.REACT_APP_BASEURL,
   responseType: "json",
   timeout: 180000,
})

export const blogApi = axios.create({
   baseURL: config.REACT_APP_BLOG_BASEURL,
   responseType: "json",
   timeout: 30000,
   headers: {
      Authorization: `Bearer ${config.REACT_APP_BLOG_API_TOKEN}`,
   },
})

export const trackApi = axios.create({
   baseURL: config.REACT_APP_TRACKING_BASEURL,
   responseType: "json",
   timeout: 30000,
   headers: {
      "x-gotruck-signature": signature,
   },
})

// Define the status codes that should trigger token refresh
const REFRESH_TRIGGER_STATUSES = [401]

// Function to refresh token when 401 error occurs
const refreshAuthLogic = async (failedRequest) => {
   // Don't refresh tokens for auth endpoints (login, register, etc.)
   const requestUrl = failedRequest.response.config.url || ""

   if (requestUrl.match(/^\/?(oauth|oauth\/refresh)$/)) {
      // Preserve the original error message from the API
      const originalErrorMessage = failedRequest.response?.data?.message || "Auth endpoint - no token refresh"
      return Promise.reject(new Error(originalErrorMessage))
   }

   try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN)

      if (!refreshToken) {
         throw new Error("No refresh token available")
      }

      // Send POST request to refresh token with refreshToken in body
      const response = await axios_client.post(`/oauth/refresh`, {
         refresh_token: refreshToken,
      })

      const newAccessToken = response.data.oauth.token

      if (!newAccessToken) {
         throw new Error("No access token received from refresh")
      }

      // Store new access token in localStorage
      localStorage.setItem(ACCESS_TOKEN, `Bearer ${newAccessToken}`)

      // Update the axios default headers for future requests
      axios_client.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`

      // Update failed request with new token
      failedRequest.response.config.headers.Authorization = `Bearer ${newAccessToken}`

      return Promise.resolve()
   } catch (error) {
      window.location.href = "/logout"
      return Promise.reject(error)
   }
}

// 🔹 Attach the auth refresh interceptor to both instances
createAuthRefreshInterceptor(axios_client, refreshAuthLogic, {
   statusCodes: REFRESH_TRIGGER_STATUSES,
})

axios_client.interceptors.request.use(
   (configuration) => {
      // Always use the freshest token from localStorage
      const token = localStorage.getItem(ACCESS_TOKEN)

      //exclude our signature/bearer token from Paystack requests
      if (!configuration.baseURL?.includes(config.REACT_APP_PAYSTACK_URL)) {
         configuration.headers["x-gotruck-signature"] = signature

         if (token) {
            configuration.headers.Authorization = token
         }
      }

      return configuration
   },

   (error) => Promise.reject(error),
)

axios_client.interceptors.response.use(
   function (response) {
      return response
   },
   (error) => {
      // Only clear tokens for specific 401 errors that aren't being handled by refresh interceptor
      if (error && error.response && error.response.status === 401) {
         if (error.response.data?.status === 4011) {
            // Only clear if this is not a token refresh request
            if (!error.config?.url?.includes("/oauth/refresh")) {
               ;(async () => {
                  axios_client.defaults.headers.common["Authorization"] = null
                  localStorage.removeItem(ACCESS_TOKEN)
               })()
            }
         }
      }
      return Promise.reject(error)
   },
)

const store = createStore(rootReducer, composeWithDevTools(applyMiddleware(axiosMiddleware(axios_client))))

export default store
